import { ReactNode, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ColumnResizer, useColumnWidths } from "@/components/common/useColumnWidths";
import { cn } from "@/lib/utils";

export interface DataColumn<T> {
  key: string;
  header: ReactNode;
  width: number;
  /** Return a comparable value to make the column sortable. */
  sortValue?: (row: T) => string | number | null | undefined;
  cell: (row: T, ctx: { width: number }) => ReactNode;
  align?: "left" | "right";
  className?: string;
}

const PIN_SHADOW = "shadow-[-4px_0_8px_-2px_hsl(var(--foreground)/0.06)]";

/**
 * Notion-style data table: fixed single-line rows, resizable & persisted column
 * widths, click-to-sort headers, sticky header, horizontal scroll, and an
 * optional actions column pinned to the right edge.
 */
export function DataTable<T>({
  storageKey,
  columns,
  rows,
  rowKey,
  actions,
  actionsWidth = 56,
  empty = "No entries.",
  loading,
  defaultSort,
  onRowClick,
  footer,
}: {
  storageKey: string;
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  actions?: (row: T) => ReactNode;
  actionsWidth?: number;
  empty?: ReactNode;
  loading?: boolean;
  defaultSort?: { key: string; asc: boolean };
  onRowClick?: (row: T) => void;
  footer?: ReactNode;
}) {
  const defaults = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c.width])) as Record<string, number>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns.map((c) => `${c.key}:${c.width}`).join("|")]
  );
  const { widths, startResize } = useColumnWidths<string>(storageKey, defaults, { min: 60 });
  const [sort, setSort] = useState<{ key: string; asc: boolean } | null>(defaultSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const get = col.sortValue;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const r =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" });
      return sort.asc ? r : -r;
    });
  }, [rows, sort, columns]);

  const w = (k: string) => widths[k] ?? defaults[k];
  const totalWidth = columns.reduce((s, c) => s + w(c.key), 0) + (actions ? actionsWidth : 0);
  const colSpan = columns.length + (actions ? 1 : 0);

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="table-fixed" style={{ minWidth: totalWidth }}>
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: w(c.key) }} />
            ))}
            {actions && <col style={{ width: actionsWidth }} />}
          </colgroup>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                return (
                  <TableHead key={c.key} className={cn("relative border-r border-border/60 last:border-r-0", c.align === "right" && "text-right")}>
                    {c.sortValue ? (
                      <button
                        type="button"
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-foreground max-w-full",
                          c.align === "right" && "flex-row-reverse"
                        )}
                        onClick={() =>
                          setSort(active ? { key: c.key, asc: !sort!.asc } : { key: c.key, asc: true })
                        }
                      >
                        <span className="truncate">{c.header}</span>
                        {active ? (
                          sort!.asc ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />
                        ) : (
                          <ChevronsUpDown className="w-3 h-3 opacity-40 shrink-0" />
                        )}
                      </button>
                    ) : (
                      <span className="truncate block">{c.header}</span>
                    )}
                    <ColumnResizer onPointerDown={(e) => startResize(c.key, e)} />
                  </TableHead>
                );
              })}
              {actions && <TableHead className={cn("sticky right-0 z-20 bg-card", PIN_SHADOW)} />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-sm text-muted-foreground py-8">
                  {loading ? "Loading…" : empty}

                </TableCell>
              </TableRow>
            ) : (
              sorted.map((r) => (
                <TableRow
                  key={rowKey(r)}
                  className={cn("group h-12 hover:bg-muted/50", onRowClick && "cursor-pointer")}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                >
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      className={cn(
                        "py-0 overflow-hidden whitespace-nowrap text-ellipsis border-r border-border/40 last:border-r-0",
                        c.align === "right" && "text-right",
                        c.className
                      )}
                    >
                      {c.cell(r, { width: w(c.key) })}
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell
                      className={cn("py-0 sticky right-0 z-10 bg-card group-hover:bg-muted", PIN_SHADOW)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">{actions(r)}</div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {footer}
    </div>
  );
}

/** Number of pills that fit in a cell of the given width. */
export const badgeCapacity = (width: number, per = 130) => Math.max(1, Math.floor((width - 24) / per));

/** Single-line truncated text that reveals the full value on hover. */
export function TruncCell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn("block truncate", className)} title={typeof children === "string" ? children : undefined}>
      {children}
    </span>
  );
}
